import { DistinctFilterMetadata } from "creso-core/types";
import { Fragment, useMemo } from "react";
import { ConditionalRenderer } from "@core/components/conditional-renderer";
import {
  Checkbox,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  V2Label,
} from "web-design-system";
import { ChevronDownIcon } from "lucide-react";
import { MultiFilterConfig } from "@funds/config";

export function MultiPicker({
  values,
  onPick,
  selected,
  expandAll = false,
  config,
}: {
  values: DistinctFilterMetadata["values"];
  onPick: (val: Array<string>) => void;
  selected: Array<string>;
  expandAll?: boolean;
  config: MultiFilterConfig;
}) {
  const grouped = useMemo(() => {
    const sortedValues = config.order ? config.order(values) : values;
    return sortedValues.filter(Boolean).reduce(
      (acc, v) => {
        const group = v.group || "DEFAULT_GROUP";
        acc[group] = acc[group] || [];
        acc[group].push(v);
        return acc;
      },
      {} as Record<string, DistinctFilterMetadata["values"]>
    );
  }, [values, config]);

  const isFlat = useMemo(() => {
    const keys = Object.keys(grouped);
    return keys.length === 1 && keys[0] === "DEFAULT_GROUP";
  }, [grouped]);

  return (
    <Fragment>
      <ConditionalRenderer condition={isFlat}>
        {grouped?.DEFAULT_GROUP?.map((v) => {
          return (
            <div key={v.id} className="flex py-1 items-center gap-2">
              <Checkbox
                id={v.id}
                checked={!!selected?.includes(v.id)}
                onCheckedChange={() => onPick([v.id])}
              />
              <V2Label
                className="text-sm font-normal capitalize"
                htmlFor={v.id}
              >
                {config.formatter ? config.formatter(v.name) : v.name}
              </V2Label>
            </div>
          );
        })}
      </ConditionalRenderer>
      <ConditionalRenderer condition={!isFlat}>
        <div className="flex flex-col relative">
          {Object.entries(grouped)?.map(([category, subcategories]) => (
            <Collapsible
              className="group/category"
              key={category}
              open={expandAll ? true : undefined}
            >
              <CollapsibleTrigger
                asChild
                className="flex bg-card justify-between sticky top-12 items-center w-full"
              >
                <div className="flex items-center py-1 gap-2">
                  <Checkbox
                    id={category}
                    onClick={(e) => e.stopPropagation()}
                    checked={
                      subcategories.every((sc) => selected?.includes(sc.id))
                        ? true
                        : subcategories.some((sc) => selected?.includes(sc.id))
                          ? "indeterminate"
                          : false
                    }
                    onCheckedChange={(checked) =>
                      onPick(
                        checked
                          ? subcategories
                              .filter((sc) => !selected?.includes(sc.id))
                              .map((sc) => sc.id)
                          : subcategories
                              .filter((sc) => selected?.includes(sc.id))
                              .map((sc) => sc.id)
                      )
                    }
                  />
                  <V2Label
                    onClick={(e) => e.stopPropagation()}
                    className="text-sm font-normal"
                    htmlFor={category}
                  >
                    {category}
                  </V2Label>
                  <ChevronDownIcon className="w-4 h-4 group-data-[state=open]/category:rotate-180 ml-auto" />
                </div>
              </CollapsibleTrigger>
              <CollapsibleContent className="pl-4">
                {subcategories?.map((subcategory) => (
                  <div
                    className="flex gap-2 py-1 items-center"
                    key={subcategory.id}
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      checked={!!selected?.includes(subcategory.id)}
                      id={subcategory.id}
                      onCheckedChange={() => onPick([subcategory.id])}
                    />
                    <V2Label
                      className="text-sm font-normal"
                      htmlFor={subcategory.id}
                    >
                      {config.formatter
                        ? config.formatter(subcategory.name)
                        : subcategory.name}
                    </V2Label>
                  </div>
                ))}
              </CollapsibleContent>
            </Collapsible>
          ))}
        </div>
      </ConditionalRenderer>
    </Fragment>
  );
}
